import { useState, useEffect, useRef } from 'react';

/**
 * Umbral por debajo del cual NO se muestra el contador.
 *
 * Un cronómetro que aparece y desaparece en respuestas de dos segundos es ruido,
 * no información. NN/g sitúa en ~1 s el límite por debajo del cual el usuario no
 * necesita feedback y en ~10 s el punto donde ya hace falta un indicador con
 * estimación; 3 s deja pasar las respuestas rápidas sin parpadeo y cubre de sobra
 * las esperas que preocupan (la media medida con herramienta es de 110 s).
 */
export const DEFAULT_SHOW_AFTER_MS = 3000;

/**
 * Formatea segundos transcurridos de forma compacta y estable en anchura.
 *
 * `45s` · `1:45` · `22:07` — nunca "hace 2 minutos": el usuario está decidiendo si
 * el turno está vivo o colgado, y para eso necesita un número que suba, no prosa.
 *
 * @param {number} totalSeconds
 * @returns {string}
 */
export function formatElapsed(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  if (s < 60) return `${s}s`;
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  if (mins < 60) return `${mins}:${String(secs).padStart(2, '0')}`;
  const hours = Math.floor(mins / 60);
  return `${hours}:${String(mins % 60).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

/**
 * useElapsedTime — cuánto lleva trabajando el agente en este turno.
 *
 * Es la única parte del indicador que no puede ser falsa: no describe lo que el
 * agente hace, sólo prueba que el turno sigue en marcha. Después de #508 y #911
 * —dos issues sobre contarle al usuario cosas que no ocurrieron— esa propiedad es
 * el motivo de que exista este hook.
 *
 * El reloj arranca cuando `active` pasa a `true` y **no se reinicia por nada más**.
 * En particular NO se deriva de `agentStatus.lastUpdated`: ese campo se refresca en
 * cada `Update Status`, así que un turno que informa tres veces reiniciaría el
 * contador tres veces y el usuario vería 110 s de trabajo como tres tramos de 30.
 *
 * @param {boolean} active - true mientras el agente trabaja (el indicador está encendido).
 * @param {{ showAfterMs?: number, tickMs?: number }} [options]
 * @returns {{ seconds: number, label: string | null }} `label` es null mientras no
 *   se alcanza `showAfterMs`, para que el consumidor no tenga que repetir el umbral.
 */
export default function useElapsedTime(active, options = {}) {
  const { showAfterMs = DEFAULT_SHOW_AFTER_MS, tickMs = 1000 } = options;

  const [seconds, setSeconds] = useState(0);
  const startedAtRef = useRef(null);

  useEffect(() => {
    if (!active) {
      // Apagado: el reloj se olvida, para que el siguiente turno empiece en cero.
      startedAtRef.current = null;
      setSeconds(0);
      return undefined;
    }

    // Encendido: si ya había reloj, se conserva — este efecto se re-ejecuta sólo
    // cuando cambia `active`, nunca cuando cambia la tarea.
    if (startedAtRef.current === null) {
      startedAtRef.current = Date.now();
      setSeconds(0);
    }

    const id = setInterval(() => {
      if (startedAtRef.current === null) return;
      setSeconds((Date.now() - startedAtRef.current) / 1000);
    }, tickMs);

    return () => clearInterval(id);
  }, [active, tickMs]);

  return {
    seconds: Math.floor(seconds),
    label: seconds * 1000 >= showAfterMs ? formatElapsed(seconds) : null,
  };
}
